import { useState, useEffect, useRef } from 'react'

interface UseEditableItemProps {
  initialContent: string
  isEditing: boolean
}

interface UseEditableItemReturn {
  editValue: string
  setEditValue: (value: string) => void
  isSaving: boolean
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>
  handleSave: (
    content: string,
    onSave: (value: string) => Promise<void>
  ) => Promise<void>
  handleCancel: (content: string) => void
  handleKeyDown: (
    e: React.KeyboardEvent,
    content: string,
    onSave: (value: string) => Promise<void>,
    onCancel: () => void
  ) => void
}

/**
 * 編集可能な項目のロジックを管理するカスタムフック
 */
export const useEditableItem = ({
  initialContent,
  isEditing,
}: UseEditableItemProps): UseEditableItemReturn => {
  const [editValue, setEditValue] = useState(initialContent)
  const [isSaving, setIsSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  // 編集開始時に値を同期してフォーカス
  useEffect(() => {
    if (isEditing) {
      setEditValue(initialContent)
      // 編集開始時にフォーカス
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus()
          // select()メソッドが存在する場合のみ実行
          if (
            'select' in inputRef.current &&
            typeof inputRef.current.select === 'function'
          ) {
            inputRef.current.select()
          } else {
            // textareaの場合は手動で全選択
            inputRef.current.setSelectionRange(0, inputRef.current.value.length)
          }
        }
      }, 100)
    }
  }, [isEditing, initialContent])

  const handleSave = async (
    content: string,
    onSave: (value: string) => Promise<void>
  ): Promise<void> => {
    if (editValue.trim() === '') {
      // 空の場合はキャンセル扱い
      handleCancel(content)
      return
    }

    if (editValue === content) {
      // 変更がない場合は編集を終了
      return
    }

    setIsSaving(true)
    try {
      await onSave(editValue)
    } catch (error) {
      // エラー時は元の値に戻す
      setEditValue(content)
      throw error
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = (content: string): void => {
    setEditValue(content)
  }

  const handleKeyDown = (
    e: React.KeyboardEvent,
    content: string,
    onSave: (value: string) => Promise<void>,
    onCancel: () => void
  ): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave(content, onSave)
        .then(() => onCancel())
        .catch(() => {})
    } else if (e.key === 'Escape') {
      e.preventDefault()
      handleCancel(content)
      onCancel()
    }
  }

  return {
    editValue,
    setEditValue,
    isSaving,
    inputRef,
    handleSave,
    handleCancel,
    handleKeyDown,
  }
}
